import { Tabs as ChakraTabs } from "@chakra-ui/react";
import {
  Children,
  Fragment,
  cloneElement,
  isValidElement,
  type ComponentProps,
  type ReactElement,
  type ReactNode,
} from "react";
import type { TabListSharedProps } from "./TabList.props";

export default function TabList({
  bgColor,
  border,
  children,
  ...props
}: TabListProps): JSX.Element {
  const indexRef = { current: 0 };

  const addIndexedValues = (node: unknown): ReactNode =>
    Children.map(node as ReactNode, (child) => {
      if (!isValidElement(child)) return child;

      if (child.type === Fragment) {
        return <>{addIndexedValues(child.props.children)}</>;
      }

      const { current } = indexRef;
      const nextValue =
        child.props.value !== undefined ? child.props.value : String(current);
      if (child.props.value === undefined) {
        indexRef.current += 1;
      }

      return cloneElement(child as ReactElement<{ value?: string }>, {
        value: nextValue,
      });
    });

  return (
    <ChakraTabs.List
      borderRadius="xs"
      bgColor={bgColor ?? "light.100"}
      borderColor="gray.400"
      borderBottom={border ?? "1px solid"}
      height="element.4xl"
      paddingTop="sm"
      width="100%"
      outline="1px solid transparent"
      {...props}
    >
      {addIndexedValues(children)}
    </ChakraTabs.List>
  );
}

export type TabListProps = TabListSharedProps &
  ComponentProps<typeof ChakraTabs.List> & {
    // Add any web specific props here.
  };
